import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAllEdges, useConcepts, useExercises, useSettings } from '@/db/hooks'
import { Button, Sheet } from '@/components/ui'
import { patchSettings } from '@/lib/settingsWrite'
import {
  SCOPE_WORD,
  TEST_LENGTHS,
  TEST_SCOPES,
  setupFrom,
  testPool,
  type TestLength,
  type TestSetup,
} from '@/lib/learnTest'
import { useStartTest } from './parts'

const Label = ({ children }: { children: string }) => (
  <div className="text-xs text-faint mt-4 mb-2">{children}</div>
)

/** "Test me": scope, length and the timer. The last choices are kept in Settings. */
export const TestSetupSheet = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const settings = useSettings()
  const concepts = useConcepts() ?? []
  const exercises = useExercises() ?? []
  const edges = useAllEdges()
  const startTest = useStartTest()
  const navigate = useNavigate()
  const [edit, setEdit] = useState<Partial<TestSetup>>({})
  const [none, setNone] = useState(false)
  const setup: TestSetup = { ...setupFrom(settings.learn?.test), ...edit }
  const set = (p: Partial<TestSetup>) => {
    setNone(false)
    setEdit((e) => ({ ...e, ...p }))
  }
  const pool = testPool({ concepts, exercises, edges }, setup)
  const canStart = pool.length > 0 && (setup.scope !== 'pick' || setup.patterns.length > 0)
  const picks = [...concepts]
    .filter((c) => !c.deleted_at)
    .sort(
      (a, b) =>
        (a.spec.category ?? '').localeCompare(b.spec.category ?? '') ||
        a.spec.name.localeCompare(b.spec.name),
    )
  const go = async () => {
    // remembered first (a write to the device), then the test
    void patchSettings({
      learn: {
        ...settings.learn,
        test: { scope: setup.scope, patterns: setup.patterns, length: setup.length, timer: setup.timer },
      },
    })
    if (await startTest(setup)) {
      onClose()
      void navigate({ to: '/learn/deck' })
    } else setNone(true)
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Test me"
      footer={
        <Button
          variant="primary"
          big
          className="w-full min-h-12"
          disabled={!canStart}
          onClick={() => void go()}
          data-testid="test-start"
        >
          Start the test
        </Button>
      }
    >
      <div data-testid="test-setup">
        <Label>What to test</Label>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Scope">
          {TEST_SCOPES.map((s) => (
            <Button
              key={s}
              variant={setup.scope === s ? 'chipOn' : 'chip'}
              onClick={() => set({ scope: s })}
              data-testid={`test-scope-${s}`}
            >
              {SCOPE_WORD[s]}
            </Button>
          ))}
        </div>
        {setup.scope === 'pick' && (
          <div
            className="flex flex-wrap gap-2 mt-3"
            role="group"
            aria-label="Patterns"
            data-testid="test-picks"
          >
            {picks.map((c) => (
              <Button
                key={c.id}
                variant={setup.patterns.includes(c.id) ? 'chipOn' : 'chip'}
                onClick={() =>
                  set({
                    patterns: setup.patterns.includes(c.id)
                      ? setup.patterns.filter((x) => x !== c.id)
                      : [...setup.patterns, c.id],
                  })
                }
              >
                {c.spec.name}
              </Button>
            ))}
          </div>
        )}
        <Label>How many questions</Label>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Length">
          {([...TEST_LENGTHS, 0] as TestLength[]).map((n) => (
            <Button
              key={n}
              variant={setup.length === n ? 'chipOn' : 'chip'}
              onClick={() => set({ length: n })}
              data-testid={`test-length-${n}`}
            >
              {n === 0 ? 'Endless' : String(n)}
            </Button>
          ))}
        </div>
        <Label>Timer</Label>
        <div className="flex gap-2" role="group" aria-label="Timer">
          <Button
            variant={setup.timer ? 'chipOn' : 'chip'}
            onClick={() => set({ timer: true })}
            data-testid="test-timer-on"
          >
            Timer on
          </Button>
          <Button
            variant={!setup.timer ? 'chipOn' : 'chip'}
            onClick={() => set({ timer: false })}
            data-testid="test-timer-off"
          >
            No timer
          </Button>
        </div>
        {(!pool.length || none) && (
          <p className="mt-4 text-sm text-muted" data-testid="test-none">
            {setup.scope === 'weakest'
              ? 'Nothing is marked as weak yet. Try all patterns.'
              : 'There are no questions for that yet.'}
          </p>
        )}
      </div>
    </Sheet>
  )
}
